'use client';

import InputField from '@/components/_rebrand/InputField';
import {
  HowAreYouManagingThisWorkTodayOptions,
  HubspotFieldLabels,
  HubspotFieldNames,
  HubspotHowDidYouHearAboutUsOptions,
  RoughlyHowManyPeopleWillUseFrontOptions,
  WhatAreYouHopingToImproveOptions
} from '@/constants/hubspot';
import { FormikProps } from 'formik';
import React, { useMemo } from 'react';
import { ButtonElement } from '../Button';
import { SelectField, SelectFieldOption } from '../SelectField';
import { Text } from '../Text';
import { TextCombination } from '../TextCombination';
import { DemoFormSteps } from './RequestDemo.hooks';
import { SelfServeLinks } from './SelfServeLinks';

export type HowDidYouHearAboutUsFields = {
  [HubspotFieldNames.Email]: string;
  [HubspotFieldNames.HowDidYouFindFront]: string;
  [HubspotFieldNames.HowDidYouFindFrontOther]: string;
}

type ConfirmationProps = {
  currentStep: DemoFormSteps;
  userFirstName: string;
  onClose?: () => void;
  meetingOffered: boolean;
  formik: FormikProps<HowDidYouHearAboutUsFields>;
}

// The "How did you find Front" form's fields should be randomized to avoid users satisficing on the first option.
const randomizeDropdownOptions = (options: Record<string, string>) => {
  const randomizedOptions: Record<string, string> = {};

  // The options are an object where keys and values are the same. We need to randomize the values.
  Object.values(options).sort(() => Math.random() - 0.5).forEach((option) => {
    // There is an "Other" option which should always be the last option.
    if (option !== "Other") {
      randomizedOptions[option] = option;
    }
  });
  randomizedOptions["Other"] = "Other";

  return Object.entries(randomizedOptions).map(([label, value]) => ({ label, value }));
}

const Confirmation: React.FC<ConfirmationProps> = ({ formik, userFirstName, currentStep, onClose, meetingOffered = false }) => {
  const heading = `Thank you, ${userFirstName ?? 'friend'}!`;
  const subheading = meetingOffered ? "We look forward to meeting with you." : "Here's what you can expect next.";
  const randomizedDropdownOptions = useMemo(() => {
    return randomizeDropdownOptions(HubspotHowDidYouHearAboutUsOptions);
  }, []);
  const showAdditionalDetailsField =
    formik.values[HubspotFieldNames.HowDidYouFindFront] === 'Podcast' ||
    formik.values[HubspotFieldNames.HowDidYouFindFront] === 'Billboard or outdoor signage' ||
    formik.values[HubspotFieldNames.HowDidYouFindFront] === 'Other';

  return (
    <div className="flex flex-col gap-lg my-auto">
      <TextCombination align="left" type="h3p2" title={heading} description={subheading} />
      {!meetingOffered && (
        <TextCombination
          type="p2"
          align="left"
          title="We’ll be in touch"
          description="A product expert will call or email you soon to set up a time to chat. We’ll ask about your workflows to prepare a personalized demo that fits your team’s unique needs." />
      )
      }
      <Text type="p2" className='text-secondary'>In the meantime, there are a few ways to explore Front on your own.</Text>

      <SelfServeLinks onClose={onClose} />

      {currentStep === DemoFormSteps.ConfirmationSubmitted ? (
        <div>
          <h4 className='text-p2 mt-3 mb-[4px] font-600'>Thank you for your feedback!</h4>
        </div>
      ) : (
        <form id="how-did-you-hear-about-us-form" className="flex flex-col py-md gap-md" onSubmit={formik.handleSubmit} noValidate>
          <SelectField
            name={HubspotFieldNames.HowDidYouFindFront}
            label={HubspotFieldLabels.HowDidYouHearAboutUs}
            placeholder="Select one"
            options={randomizedDropdownOptions as SelectFieldOption[]}
            value={formik.values[HubspotFieldNames.HowDidYouFindFront]}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            errorMessage={formik.errors[HubspotFieldNames.HowDidYouFindFront] as string}
          />
          {showAdditionalDetailsField && (
            <InputField
              type="text"
              name={HubspotFieldNames.HowDidYouFindFrontOther}
              label={HubspotFieldLabels.HowDidYouFindFrontOther}
              placeholder="Please provide additional details"
              value={formik.values[HubspotFieldNames.HowDidYouFindFrontOther]}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              errorMessage={formik.errors[HubspotFieldNames.HowDidYouFindFrontOther] as string}
            />
          )}
          <SelectField
            name={HubspotFieldNames.WhatAreYouHopingToImprove}
            label={HubspotFieldLabels.WhatAreYouHopingToImprove}
            placeholder="Select one"
            options={Object.entries(WhatAreYouHopingToImproveOptions).map(([label, value]) => ({ label, value }))}
            value={formik.values[HubspotFieldNames.WhatAreYouHopingToImprove]}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            errorMessage={formik.errors[HubspotFieldNames.WhatAreYouHopingToImprove] as string}
          />
          <SelectField
            name={HubspotFieldNames.HowAreYouManagingThisWorkToday}
            label={HubspotFieldLabels.HowAreYouManagingThisWorkToday}
            placeholder="Select one"
            options={Object.entries(HowAreYouManagingThisWorkTodayOptions).map(([label, value]) => ({ label, value }))}
            value={formik.values[HubspotFieldNames.HowAreYouManagingThisWorkToday]}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            errorMessage={formik.errors[HubspotFieldNames.HowAreYouManagingThisWorkToday] as string}
          />
          <SelectField
            name={HubspotFieldNames.RoughlyHowManyPeopleWillUseFront}
            label={HubspotFieldLabels.RoughlyHowManyPeopleWillUseFront}
            placeholder="Select one"
            options={Object.entries(RoughlyHowManyPeopleWillUseFrontOptions).map(([label, value]) => ({ label, value }))}
            value={formik.values[HubspotFieldNames.RoughlyHowManyPeopleWillUseFront]}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            errorMessage={formik.errors[HubspotFieldNames.RoughlyHowManyPeopleWillUseFront] as string}
          />
          <ButtonElement
            variant="primary"
            buttonType="filled"
            text={formik.isValidating || formik.isSubmitting ? 'Submitting' : 'Submit'}
            disabled={formik.isValidating || formik.isSubmitting} />
        </form>
      )}
    </div>
  );
};

export default Confirmation;
